import { Storage } from '@ionic/storage';

// Storage keys
const STORAGE_KEYS = {
  AUTH_TOKEN: 'auth.token',
  USER_DATA: 'auth.user',
  DEVICE_ID: 'device.id',
  FCM_TOKEN: 'fcm.token',
  PHONE_NUMBER: 'auth.phone_number',
  DEVICE_REGISTERED_EMPLOYEE_ID: 'device.registered_employee_id',
} as const;

class StorageService {
  private storage: Storage | null = null;
  private isInitialized = false;

  async initialize(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    try {
      // Create storage instance
      this.storage = new Storage();
      
      // Create storage
      await this.storage.create();
      
      this.isInitialized = true;
      console.log('Storage service initialized successfully');
    } catch (error) {
      console.error('Failed to initialize storage service:', error);
      // Fallback to localStorage if Ionic Storage fails
      this.storage = null;
    }
  }

  private async ensureInitialized(): Promise<void> {
    if (!this.isInitialized) {
      await this.initialize();
    }
  }

  // Generic storage methods
  async get<T>(key: string): Promise<T | null> {
    await this.ensureInitialized();
    
    try {
      if (this.storage) {
        return await this.storage.get(key);
      } else {
        // Fallback to localStorage
        const item = localStorage.getItem(key);
        return item ? JSON.parse(item) : null;
      }
    } catch (error) {
      console.error(`Error getting item ${key}:`, error);
      return null;
    }
  }

  async set<T>(key: string, value: T): Promise<void> {
    await this.ensureInitialized();
    
    try {
      if (this.storage) {
        await this.storage.set(key, value);
      } else {
        // Fallback to localStorage
        localStorage.setItem(key, JSON.stringify(value));
      }
    } catch (error) {
      console.error(`Error setting item ${key}:`, error);
    }
  }

  async remove(key: string): Promise<void> {
    await this.ensureInitialized();
    
    try {
      if (this.storage) {
        await this.storage.remove(key);
      } else {
        // Fallback to localStorage
        localStorage.removeItem(key);
      }
    } catch (error) {
      console.error(`Error removing item ${key}:`, error);
    }
  }

  async clear(): Promise<void> {
    await this.ensureInitialized();
    
    try {
      if (this.storage) {
        await this.storage.clear();
      } else {
        // Fallback to localStorage
        localStorage.clear();
      }
    } catch (error) {
      console.error('Error clearing storage:', error);
    }
  }

  // Auth-specific methods
  async getAuthToken(): Promise<string | null> {
    return this.get<string>(STORAGE_KEYS.AUTH_TOKEN);
  }

  async setAuthToken(token: string): Promise<void> {
    await this.set(STORAGE_KEYS.AUTH_TOKEN, token);
    console.log('Auth token stored successfully');
  }

  async removeAuthToken(): Promise<void> {
    await this.remove(STORAGE_KEYS.AUTH_TOKEN);
    console.log('Auth token removed');
  }

  async getUserData(): Promise<any | null> {
    return this.get(STORAGE_KEYS.USER_DATA);
  }

  async setUserData(userData: any): Promise<void> {
    await this.set(STORAGE_KEYS.USER_DATA, userData);
    console.log('User data stored successfully');
  }

  async removeUserData(): Promise<void> {
    await this.remove(STORAGE_KEYS.USER_DATA);
    console.log('User data removed');
  }

  async getDeviceId(): Promise<string | null> {
    return this.get<string>(STORAGE_KEYS.DEVICE_ID);
  }

  async setDeviceId(deviceId: string): Promise<void> {
    await this.set(STORAGE_KEYS.DEVICE_ID, deviceId);
  }

  async getFcmToken(): Promise<string | null> {
    return this.get<string>(STORAGE_KEYS.FCM_TOKEN);
  }

  async setFcmToken(token: string): Promise<void> {
    await this.set(STORAGE_KEYS.FCM_TOKEN, token);
  }

  async getPhoneNumber(): Promise<string | null> {
    return this.get<string>(STORAGE_KEYS.PHONE_NUMBER);
  }

  async setPhoneNumber(phoneNumber: string): Promise<void> {
    await this.set(STORAGE_KEYS.PHONE_NUMBER, phoneNumber);
    console.log('Phone number stored successfully');
  }

  async getDeviceRegisteredEmployeeId(): Promise<number | null> {
    const value = await this.get<number>(STORAGE_KEYS.DEVICE_REGISTERED_EMPLOYEE_ID);
    if (value == null) return null;
    const num = typeof value === 'number' ? value : Number(value);
    return Number.isNaN(num) ? null : num;
  }

  async setDeviceRegisteredEmployeeId(employeeId: number | null): Promise<void> {
    if (employeeId == null) {
      await this.remove(STORAGE_KEYS.DEVICE_REGISTERED_EMPLOYEE_ID);
    } else {
      await this.set(STORAGE_KEYS.DEVICE_REGISTERED_EMPLOYEE_ID, employeeId);
    }
  }

  // Clear all auth data
  async clearAuthData(): Promise<void> {
    await this.removeAuthToken();
    await this.removeUserData();
    await this.setDeviceRegisteredEmployeeId(null);
    console.log('All auth data cleared');
  }
}

export const storageService = new StorageService();
export { STORAGE_KEYS };
